import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { PageHero, PageCta } from "../components/PageHero";
import { Reveal, FadeIn } from "../components/motion";

export default function Strategie() {
  const { t } = useLanguage();
  const p = t.pages.strategie;
  const reduce = useReducedMotion();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", reduce ? "0%" : "8%"]);

  return (
    <main data-testid="strategie-page">
      <PageHero testId="strategie-hero" label={p.label} title={p.title} sub={p.sub} />

      <section data-testid="strategie-intro" className="bg-paper">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <h2 className="font-head text-3xl leading-tight text-ink sm:text-5xl">{p.introTitle}</h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-8 text-lg font-light leading-relaxed text-ink/80 sm:text-xl">{p.introText}</p>
              </Reveal>
            </div>
            <FadeIn className="lg:col-span-5 lg:col-start-8">
              <div ref={imgRef} className="overflow-hidden">
                <motion.img
                  src="/images/wave-crest.jpg"
                  alt="Brechende Welle mit weißem Schaum"
                  loading="lazy"
                  style={{ y: imgY }}
                  className="aspect-square w-full scale-110 object-cover will-change-transform"
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section data-testid="strategie-characteristics" className="grain relative overflow-hidden bg-[#053B4D] text-white">
        <div className="mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
          <Reveal y={14}>
            <p className="text-[13px] font-medium uppercase tracking-label text-wave">{p.charsLabel}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-head mt-8 max-w-3xl text-3xl leading-tight sm:text-5xl">{p.charsTitle}</h2>
          </Reveal>
          <div className="mt-20 grid gap-px bg-ink sm:grid-cols-2 lg:grid-cols-3">
            {p.chars.map((ch, i) => (
              <Reveal
                key={ch.title}
                delay={i * 0.07}
                className="group bg-deep p-10 transition-colors duration-700 hover:bg-[#DEE9EF] sm:p-12"
              >
                <span className="font-head text-lg text-wave transition-colors duration-700 group-hover:text-deep">0{i + 1}</span>
                <h3 className="font-head mt-6 text-xl leading-snug text-white transition-colors duration-700 group-hover:text-ink sm:text-2xl">{ch.title}</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-white/60 transition-colors duration-700 group-hover:text-ink/70">{ch.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PageCta title={p.ctaTitle} text={p.ctaText} />
    </main>
  );
}
